import type { ContextBlock, DraftRequest, PipelineStage, StageResult } from '../types';
import { getParserFor } from '../../parsing/tree-sitter';
import { extractConstruct } from '../../parsing/extract-construct';
import { draftText } from '../draft-text';

export const a1AstExtraction: PipelineStage = {
  id: 'A1',
  group: 'A',
  label: 'AST-based context extraction',
  description:
    'When you ask about specific code, send only the enclosing function/class/module construct instead of the whole file.',
  impact: 'High',
  defaultEnabled: true,
  implemented: true,

  async run(draft, _params, ctx): Promise<StageResult> {
    const tokensBefore = ctx.estimateTokens(draftText(draft));
    const target = draft.selection ?? draft.cursor;
    if (!target) {
      return noOp(draft, tokensBefore, ['No cursor/selection provided — nothing to extract from, left files as-is.']);
    }

    const file = draft.openFiles.find((f) => f.path === target.path);
    if (!file || !file.languageId) {
      return noOp(draft, tokensBefore, [`No parseable open file matches "${target.path}".`]);
    }

    const blockId = `file:${file.path}`;
    const fullBlockIndex = draft.contextBlocks.findIndex((b) => b.id === blockId);
    if (fullBlockIndex === -1) {
      return noOp(draft, tokensBefore, [
        `"${file.path}" isn't present as a full-file context block (possibly already reduced by another stage).`,
      ]);
    }

    try {
      const parser = await getParserFor(file.languageId);
      const tree = parser.parse(file.content);
      if (!tree) throw new Error('tree-sitter returned no tree (parse failed)');

      const offset =
        'startLine' in target
          ? lineToOffset(file.content, target.startLine)
          : lineColToOffset(file.content, target.line, target.column);

      const extracted = extractConstruct(tree.rootNode, offset, file.content, file.languageId);
      const importsText = extracted.importLines.length ? extracted.importLines.join('\n') + '\n\n' : '';
      const newContent = `${importsText}${extracted.text}`;

      const newBlock: ContextBlock = {
        id: blockId,
        label: `${file.path} (${extracted.nodeType}, extracted)`,
        content: newContent,
        cacheable: true,
      };
      const newBlocks = [...draft.contextBlocks];
      newBlocks[fullBlockIndex] = newBlock;

      const newDraft: DraftRequest = { ...draft, contextBlocks: newBlocks };
      const tokensAfter = ctx.estimateTokens(draftText(newDraft));

      return {
        draft: newDraft,
        report: {
          stageId: 'A1',
          tokensBefore,
          tokensAfter,
          tokensSaved: Math.max(0, tokensBefore - tokensAfter),
          notes: [
            `Extracted "${extracted.nodeType}" from ${file.path} (${extracted.text.length} of ${file.content.length} chars) instead of the full file.`,
          ],
        },
      };
    } catch (err) {
      // Quality guardrail: fail open rather than block the request.
      return {
        draft,
        report: {
          stageId: 'A1',
          tokensBefore,
          tokensAfter: tokensBefore,
          tokensSaved: 0,
          notes: ['Parse failed — sent the full file instead.'],
        },
        warning: err instanceof Error ? err.message : String(err),
      };
    }
  },
};

function noOp(draft: DraftRequest, tokensBefore: number, notes: string[]): StageResult {
  return {
    draft,
    report: { stageId: 'A1', tokensBefore, tokensAfter: tokensBefore, tokensSaved: 0, notes },
  };
}

function lineToOffset(text: string, line: number): number {
  const lines = text.split('\n');
  return lines.slice(0, line).reduce((acc, l) => acc + l.length + 1, 0);
}

function lineColToOffset(text: string, line: number, column: number): number {
  return lineToOffset(text, line) + column;
}
